import offensiveApi from './offensiveApi';
import offensiveWebSocket from './offensiveWebSocket';
import useOffensiveStore from '../stores/offensiveStore';

/**
 * Technique Execution Service
 * Handles technique execution, monitoring, and real-time updates
 */
class TechniqueExecutionService {
  constructor() {
    this.activeExecutions = new Map(); // techniqueId -> execution details
    this.outputSubscriptions = new Set(); // Set of subscribed technique IDs
  }

  /**
   * Execute a technique
   * @param {string} engagementId - Engagement ID
   * @param {string} techniqueId - Technique ID
   * @param {Object} parameters - Execution parameters
   * @returns {Promise<Object>} Execution result
   */
  async executeTechnique(engagementId, techniqueId, parameters = {}) {
    try {
      // Add technique to store
      const store = useOffensiveStore.getState();
      store.addTechnique({
        id: techniqueId,
        techniqueId: techniqueId,
        engagementId: engagementId,
        status: 'queued',
        parameters: parameters,
        startTime: new Date().toISOString()
      });

      // Start execution via API
      const result = await offensiveApi.executeTechnique(engagementId, techniqueId);
      
      // Update technique status
      store.updateTechnique(techniqueId, {
        status: 'running',
        startTime: new Date().toISOString()
      });

      // Subscribe to output
      this.subscribeToOutput(techniqueId);

      // Track active execution
      this.activeExecutions.set(techniqueId, {
        engagementId,
        techniqueId,
        startTime: Date.now(),
        parameters
      });

      return result;
    } catch (error) {
      console.error('Error executing technique:', error);
      
      // Update technique status to failed
      const store = useOffensiveStore.getState();
      store.updateTechnique(techniqueId, {
        status: 'failed',
        endTime: new Date().toISOString(),
        error: error.message
      });

      throw error;
    }
  }

  /**
   * Stop technique execution
   * @param {string} engagementId - Engagement ID
   * @param {string} techniqueId - Technique ID
   * @returns {Promise<Object>} Stop result
   */
  async stopTechnique(engagementId, techniqueId) {
    try {
      // Stop execution via API
      const result = await offensiveApi.stopTechnique(engagementId, techniqueId);
      
      // Update technique status
      const store = useOffensiveStore.getState();
      store.updateTechnique(techniqueId, {
        status: 'stopped',
        endTime: new Date().toISOString()
      });

      // Unsubscribe from output
      this.unsubscribeFromOutput(techniqueId);

      // Remove from active executions
      this.activeExecutions.delete(techniqueId);

      return result;
    } catch (error) {
      console.error('Error stopping technique:', error);
      throw error;
    }
  }

  /**
   * Subscribe to technique output
   * @param {string} techniqueId - Technique ID
   */
  subscribeToOutput(techniqueId) {
    if (!this.outputSubscriptions.has(techniqueId)) {
      this.outputSubscriptions.add(techniqueId);
      offensiveWebSocket.subscribeToTechniqueOutput(techniqueId);
    }
  }

  /**
   * Unsubscribe from technique output
   * @param {string} techniqueId - Technique ID
   */
  unsubscribeFromOutput(techniqueId) {
    if (this.outputSubscriptions.has(techniqueId)) {
      this.outputSubscriptions.delete(techniqueId);
      offensiveWebSocket.unsubscribeFromTechniqueOutput(techniqueId);
    }
  }

  /**
   * Get technique output
   * @param {string} engagementId - Engagement ID
   * @param {string} techniqueId - Technique ID
   * @returns {Promise<Object>} Technique output
   */
  async getTechniqueOutput(engagementId, techniqueId) {
    try {
      const output = await offensiveApi.getTechniqueOutput(engagementId, techniqueId);
      
      // Update technique in store
      const store = useOffensiveStore.getState();
      store.updateTechnique(techniqueId, {
        output: output.content || '',
        lastOutputUpdate: new Date().toISOString()
      });

      return output;
    } catch (error) {
      console.error('Error fetching technique output:', error);
      throw error;
    }
  }

  /**
   * Get all techniques for an engagement
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Array>} List of techniques
   */
  async getEngagementTechniques(engagementId) {
    try {
      const techniques = await offensiveApi.getTechniques(engagementId);
      
      // Update store with techniques
      const store = useOffensiveStore.getState();
      techniques.forEach(technique => {
        store.addTechnique(technique);
      });

      return techniques;
    } catch (error) {
      console.error('Error fetching engagement techniques:', error);
      throw error;
    }
  }

  /**
   * Get techniques for a specific tactic
   * @param {string} engagementId - Engagement ID
   * @param {string} tacticId - Tactic ID
   * @returns {Promise<Array>} List of techniques
   */
  async getTacticTechniques(engagementId, tacticId) {
    try {
      const techniques = await offensiveApi.getTechniques(engagementId, tacticId);
      return techniques;
    } catch (error) {
      console.error('Error fetching tactic techniques:', error);
      throw error;
    }
  }

  /**
   * Add a custom technique
   * @param {string} engagementId - Engagement ID
   * @param {Object} techniqueData - Technique data
   * @returns {Promise<Object>} Created technique
   */
  async addCustomTechnique(engagementId, techniqueData) {
    try {
      const technique = await offensiveApi.addTechnique(engagementId, techniqueData);
      
      // Add to store
      const store = useOffensiveStore.getState();
      store.addTechnique(technique);

      return technique;
    } catch (error) {
      console.error('Error adding custom technique:', error);
      throw error;
    }
  }

  /**
   * Get available tools and techniques
   * @returns {Promise<Object>} Available tools and techniques
   */
  async getAvailableTools() {
    try {
      const tools = await offensiveApi.getAvailableTools();
      return tools;
    } catch (error) {
      console.error('Error fetching available tools:', error);
      throw error;
    }
  }

  /**
   * Validate target before execution
   * @param {string} target - Target to validate
   * @returns {Promise<Object>} Validation result
   */
  async validateTarget(target) {
    try {
      const result = await offensiveApi.validateTarget(target);
      return result;
    } catch (error) {
      console.error('Error validating target:', error);
      throw error;
    }
  }

  /**
   * Get execution statistics
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Object>} Execution statistics
   */
  async getExecutionStats(engagementId) {
    try {
      const stats = await offensiveApi.getEngagementStats(engagementId);
      return stats;
    } catch (error) {
      console.error('Error fetching execution stats:', error);
      throw error;
    }
  }

  /**
   * Handle WebSocket events for technique execution
   * @param {Object} event - WebSocket event
   */
  handleWebSocketEvent(event) {
    const { type, data } = event;

    switch (type) {
      case 'technique_started':
        this.handleTechniqueStarted(data);
        break;
        
      case 'technique_output':
        this.handleTechniqueOutput(data);
        break;
        
      case 'technique_completed':
        this.handleTechniqueCompleted(data);
        break;
        
      case 'technique_failed':
        this.handleTechniqueFailed(data);
        break;
        
      case 'technique_stopped':
        this.handleTechniqueStopped(data);
        break;
        
      default:
        console.log('Unhandled technique event:', type);
    }
  }

  /**
   * Handle technique started event
   * @param {Object} data - Event data
   */
  handleTechniqueStarted(data) {
    const store = useOffensiveStore.getState();
    store.updateTechnique(data.technique_id, {
      status: 'running',
      startTime: data.timestamp
    });
  }

  /**
   * Handle technique output event
   * @param {Object} data - Event data
   */
  handleTechniqueOutput(data) {
    const store = useOffensiveStore.getState();
    store.appendTerminalOutput(data.technique_id, data.output);
  }

  /**
   * Handle technique completed event
   * @param {Object} data - Event data
   */
  handleTechniqueCompleted(data) {
    const store = useOffensiveStore.getState();
    store.updateTechnique(data.technique_id, {
      status: 'completed',
      endTime: data.timestamp,
      progress: 100
    });

    // Add findings if any
    if (data.findings && data.findings.length > 0) {
      data.findings.forEach(finding => {
        store.addFinding({
          ...finding,
          techniqueId: data.technique_id,
          engagementId: data.engagement_id
        });
      });
    }

    // Remove from active executions
    this.activeExecutions.delete(data.technique_id);
  }

  /**
   * Handle technique failed event
   * @param {Object} data - Event data
   */
  handleTechniqueFailed(data) {
    const store = useOffensiveStore.getState();
    store.updateTechnique(data.technique_id, {
      status: 'failed',
      endTime: data.timestamp,
      error: data.error
    });

    // Remove from active executions
    this.activeExecutions.delete(data.technique_id);
  }

  /**
   * Handle technique stopped event
   * @param {Object} data - Event data
   */
  handleTechniqueStopped(data) {
    const store = useOffensiveStore.getState();
    store.updateTechnique(data.technique_id, {
      status: 'stopped',
      endTime: data.timestamp
    });

    // Remove from active executions
    this.activeExecutions.delete(data.technique_id);
  }

  /**
   * Get active executions
   * @returns {Array} List of active executions
   */
  getActiveExecutions() {
    return Array.from(this.activeExecutions.values());
  }

  /**
   * Check if technique is executing
   * @param {string} techniqueId - Technique ID
   * @returns {boolean} True if executing
   */
  isExecuting(techniqueId) {
    return this.activeExecutions.has(techniqueId);
  }

  /**
   * Cleanup all active executions
   */
  cleanup() {
    // Unsubscribe from all outputs
    this.outputSubscriptions.forEach(techniqueId => {
      offensiveWebSocket.unsubscribeFromTechniqueOutput(techniqueId);
    });
    
    this.outputSubscriptions.clear();
    this.activeExecutions.clear();
  }
}

// Create and export singleton instance
const techniqueExecutionService = new TechniqueExecutionService();
export default techniqueExecutionService;
